import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Checkbox } from "../components/ui/checkbox";
import AddUsersToPolicyModal from "../components/AddUsersToPolicyModal";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  ArrowLeft,
  UserPlus,
  UserMinus,
  Trash2,
  BellRing,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  Users,
  Building2,
  Undo2,
  AlertCircle,
  Mail
} from "lucide-react";
import { toast } from "sonner";
import { Policy, AssignmentUser, AssignmentDetails, Memo } from "../types";
import { toSydneyDate, formatTimestamp } from "../utils/helpers";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";

export default function PolicyAssignPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { getUserById, getDepartmentName, policyAssignments } = useData();

  const [assignment, setAssignment] = useState<AssignmentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [showRemoveUsersModal, setShowRemoveUsersModal] = useState(false);
  const [showRecallDialog, setShowRecallDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [policy, setPolicy] = useState<Policy>();
  const [memo, setMemo] = useState<Memo>();
  const [showDeleteAssignmentDialog, setShowDeleteAssignmentDialog] = useState(false);
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [isSendingReminders, setIsSendingReminders] = useState(false);

  // Calculate overdue days - returns undefined if not overdue, number of days if overdue
  const calculateOverdueDays = (dueDate: string): number | undefined => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = now.getTime() - due.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : undefined;
  };

  // Calculate due date based on priority and creation date
  // This implements business logic for assignment deadlines
  const calculateDueDate = (createdDate: string, priority: 'Low' | 'Med' | 'High' | 'Critical'): string => {
    // Business rules for assignment deadlines based on priority
    const daysToAdd = {
      'Critical': 3,  // 3 days - urgent compliance/safety items
      'High': 7,      // 1 week - important policies  
      'Med': 14,      // 2 weeks - standard policies
      'Low': 30       // 1 month - general information
    };

    const created = new Date(createdDate);
    const due = new Date(created);
    due.setDate(due.getDate() + daysToAdd[priority]);

    // Set to end of day (23:59:59) for clear deadline
    due.setHours(23, 59, 59, 999);

    return due.toISOString();
  };



  const policyId = searchParams.get('policyId');
  const memoId = searchParams.get('memoId');
  const documentId = policyId || memoId;
  const documentType = policyId ? 'Policy' : 'Memo';

  const loadAssignmentDetails = async () => {

    if (!documentId) return;

    setIsLoading(true);
    try {
      let document: any = null;
      let assignments: any[] = [];

      // Fetch the document and its assignments
      if (documentType === 'Policy') {
        // Fetch policy details
        const policyResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/policies/${documentId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (policyResponse.ok) {
          const policyData = await policyResponse.json();
          document = policyData.policy;
          setPolicy(document);
          setActivityLogs(document.activityLogs);
        }

        // Fetch policy assignments
        const assignmentResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/policies/${documentId}/assignments`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (assignmentResponse.ok) {
          const assignmentData = await assignmentResponse.json();
          assignments = assignmentData.assignments || [];
        }
      } else {
        // Fetch memo details
        const memoResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/memos/${documentId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (memoResponse.ok) {
          const memoData = await memoResponse.json();
          document = memoData.memo;
          setMemo(document);
          setActivityLogs(document.activityLogs);
        }

        // Fetch memo assignments
        const assignmentResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/memos/${documentId}/assignments`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (assignmentResponse.ok) {
          const assignmentData = await assignmentResponse.json();
          assignments = assignmentData.assignments || [];
        }
      }

      if (!document) {
        toast.error(`${documentType} not found`);
        return;
      }

      // Transform assignments to match the expected format
      const transformedUsers: AssignmentUser[] = assignments.map(assignment => {
        const user = assignment.user;
        let status: 'Assigned' | 'Read' | 'Signed' | 'Overdue' = 'Assigned';

        // Determine status based on assignment data
        if (assignment.signedDate !== null) {
          status = 'Signed';
        } else if (assignment.readDate) {
          status = 'Read';
        } else if (assignment.isOverdue) {
          status = 'Overdue';
        }

        return {
          id: user.id,
          name: user.name,
          email: user.email,
          department: user.department,
          jobRole: user.jobRole,
          assignedDate: assignment.assignedDate,
          readDate: assignment.readDate,
          signedDate: assignment.signedDate,
          status: status
        };
      });

      // Calculate statistics
      const totalAssigned = assignments.length;
      const totalRead = assignments.filter(a => a.readDate).length;
      const totalSigned = assignments.filter(a => a.signedDate).length;
      const completionPercentage = document.requireSignature
        ? (totalSigned / totalAssigned) * 100
        : (totalRead / totalAssigned) * 100;

      // Get the first assignment to extract common data
      const firstAssignment = assignments[0];
      const assignmentPriority = firstAssignment?.assignmentPriority || 'Med';

      // Build the assignment details object
      const assignmentDetails: AssignmentDetails = {
        id: `assignment-${documentId}`,
        documentId: documentId,
        name: documentType === 'Policy' ? document.title : document.subject,
        type: documentType,
        version: documentType === 'Policy' ? document.version : undefined,
        number: documentType === 'Memo' ? document.number : undefined,
        requireSignature: document.requireSignature,
        effectiveDate: document.effectiveDate,
        documentStatus: document.status,
        assignmentPriority: assignmentPriority,
        createdDate: firstAssignment?.createdAt || document.createdDate || document.createdOn,
        dueDate: firstAssignment?.dueDate,
        createdBy: {
          name: firstAssignment?.assignedBy?.name || 'System',
          email: firstAssignment?.assignedBy?.email || ''
        },
        users: transformedUsers,
        stats: {
          totalAssigned: totalAssigned,
          totalRead: totalRead,
          totalSigned: totalSigned,
          completionPercentage: Math.round(completionPercentage)
        },
        recentActivity: activityLogs // Could be populated from activity logs if needed
      };

      // Calculate overdue days if there's a due date and it's actually overdue
      if (assignmentDetails.dueDate) {
        const overdueDays = calculateOverdueDays(assignmentDetails.dueDate);
        if (overdueDays !== undefined) {
          assignmentDetails.overdueDays = overdueDays;
        }
      }

      setAssignment(assignmentDetails);
    } catch (error) {
      console.error('Error loading assignment details:', error);
      toast.error('Failed to load assignment details');
    } finally {
      setIsLoading(false);
    }
  };

  // Load assignment details
  useEffect(() => {
    loadAssignmentDetails();
  }, []);

  // Derived: users eligible for reminders (not Signed or Read)
  const eligibleUsers = assignment?.users.filter(u => u.status !== 'Signed' && u.status !== 'Read') || [];
  const allEligibleSelected = eligibleUsers.length > 0 && eligibleUsers.every(u => selectedUserIds.has(u.id));

  const toggleSelectAll = () => {
    if (allEligibleSelected) {
      setSelectedUserIds(prev => {
        const next = new Set(prev);
        eligibleUsers.forEach(u => next.delete(u.id));
        return next;
      });
    } else {
      setSelectedUserIds(prev => {
        const next = new Set(prev);
        eligibleUsers.forEach(u => next.add(u.id));
        return next;
      });
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSendReminders = async () => {
    if (!assignment || selectedUserIds.size === 0) return;
    setIsSendingReminders(true);
    try {
      const endpoint = assignment.type === 'Policy' ? 'policies' : 'memos';
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/${endpoint}/${assignment.documentId}/send-reminders`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ userIds: Array.from(selectedUserIds) })
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(data.message || `Reminders sent to ${data.sent} user(s)`);
        setSelectedUserIds(new Set());
      } else {
        toast.error(data.message || 'Failed to send reminders');
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Failed to send reminders');
    } finally {
      setIsSendingReminders(false);
    }
  };

  // Handle policy recall
  const handleRecallPolicy = async () => {
    if (!policy || !currentUser) return;

    setIsProcessing(true);
    try {
      // Call backend API to recall policy
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/policies/${policy.id}/recall`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to recall policy');
      }

      setShowRecallDialog(false);
      toast.success('Policy recalled successfully. All signatures have been removed and the policy is now in Draft status.');

      // Navigate back to policies management
      setTimeout(() => {
        navigate('/policies');
      }, 1500);

    } catch (error) {
      console.error('Error recalling policy:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to recall policy');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteAssignment = async (policyId: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/policies/${policyId}/assignments`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.message);
      } else {
        toast.success('Assignment deleted successfully.');
        navigate('/policies?tab=assignments');
      }

    } catch (error) {
      console.error('Error deleting policy assignment:', error);
      toast.error('Failed to delete assignment.');
    }
  }

  // No local helpers needed, using toSydneyDate and formatTimestamp from helpers

  const getPriorityBadge = (priority: string) => {
    const variants = {
      'Low': 'bg-blue-100 text-blue-800',
      'Med': 'bg-yellow-100 text-yellow-800',
      'High': 'bg-orange-100 text-orange-800',
      'Critical': 'bg-red-100 text-red-800'
    } as const;

    return (
      <Badge className={variants[priority as keyof typeof variants] || variants.Med}>
        {priority}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      'Assigned': 'bg-blue-100 text-blue-800',
      'Read': 'bg-yellow-100 text-yellow-800',
      'Signed': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800'
    } as const;

    return (
      <Badge className={variants[status as keyof typeof variants] || variants.Assigned}>
        {status}
      </Badge>
    );
  };

  // if (!canManagePolicies) {
  //   return (
  //     <div className="p-6">
  //       <Card>
  //         <CardContent className="p-6">
  //           <div className="text-center">
  //             <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
  //             <h3 className="mb-2">Access Restricted</h3>
  //             <p className="text-muted-foreground">
  //               You don't have permission to view assignment details. Contact your administrator if you need access.
  //             </p>
  //           </div>
  //         </CardContent>
  //       </Card>
  //     </div>
  //   );
  // }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <div className="h-64 bg-gray-200 rounded"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
            <div className="space-y-6">
              <div className="h-32 bg-gray-200 rounded"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="mb-2">Assignment Not Found</h3>
              <p className="text-muted-foreground">
                The assignment you're looking for doesn't exist or has been removed.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Can I={ACTIONS.CREATE} a={RESOURCES.POLICY_ASSIGNMENT} passThrough={
      <div className="p-4 md:p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">You don't have permission to view policies.</p>
          </CardContent>
        </Card>
      </div>
    }>
      <div className="space-y-6 p-2 md:p-0">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Link to="/policies?tab=assignments">
              <Button variant="outline" size="icon" className="flex-shrink-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl md:text-2xl font-bold leading-tight" title={assignment.name}>
                {assignment.name.length > 30 ? `${assignment.name.substring(0, 30)}...` : assignment.name}
              </h1>
              <p className="text-sm text-muted-foreground">
                {assignment.version && `v${assignment.version} • `}
                {assignment.number && `${assignment.number} • `}
                Effective: {toSydneyDate(assignment.effectiveDate)}
              </p>
            </div>
          </div>

          <div className="w-full md:w-auto">
            <div className="grid grid-cols-2 md:flex md:items-center gap-2">
              <Button variant="outline" className="w-full" onClick={() => setShowRecallDialog(true)}>
                <Undo2 className="h-4 w-4 mr-2" /> Recall
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setShowAddUsersModal(true)}>
                <UserPlus className="h-4 w-4 mr-2" /> Add
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setShowRemoveUsersModal(true)}>
                <UserMinus className="h-4 w-4 mr-2" /> Remove
              </Button>
              <Button variant="destructive" className="w-full" onClick={() => setShowDeleteAssignmentDialog(true)}>
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Document</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-primary flex-shrink-0" />
                  <div>
                    <Link
                      to={assignment.type === 'Policy' ? `/policies/${assignment.documentId}` : `/memos/${assignment.documentId}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {assignment.name}
                    </Link>
                    <p className="text-sm text-muted-foreground">
                      {assignment.type} • {assignment.documentStatus}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Signature Tracking</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedUserIds.size === 0 || isSendingReminders}
                  onClick={handleSendReminders}
                >
                  {isSendingReminders ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Send Nudge
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Progress</span>
                    <span>{assignment.stats.completionPercentage}%</span>
                  </div>
                  <Progress value={assignment.stats.completionPercentage} />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>{assignment.stats.totalSigned} of {assignment.stats.totalAssigned} signed</span>
                    <span>{assignment.stats.totalRead} read</span>
                  </div>
                </div>
                <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={allEligibleSelected}
                            onCheckedChange={toggleSelectAll}
                            aria-label="Select all pending users"
                          />
                        </TableHead>
                        <TableHead>User</TableHead>
                        <TableHead className="text-right">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignment.users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="py-2">
                            <Checkbox
                              checked={selectedUserIds.has(user.id)}
                              disabled={user.status === 'Signed' || user.status === 'Read'}
                              onCheckedChange={() => toggleUserSelection(user.id)}
                              aria-label={`Select ${user.name}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{user.name}</div>
                            <div className="text-sm text-muted-foreground">{user.email}</div>
                          </TableCell>
                          <TableCell className="text-right">{getStatusBadge(user.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {assignment.users.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No users assigned.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar Column (Corrected) */}
          <div className="lg:col-span-1 space-y-6">
            {/* Assignment Details Card */}
            <Card>
              <CardHeader>
                <CardTitle>Assignment Details</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-4">
                <div>
                  <p className="font-medium text-muted-foreground">Priority</p>
                  {getPriorityBadge(assignment.assignmentPriority)}
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Created On</p>
                  <p>{formatTimestamp(assignment.createdDate)}</p>
                </div>
                {assignment.dueDate && (
                  <div>
                    <p className="font-medium text-muted-foreground">Due Date</p>
                    <p className={assignment.overdueDays ? "text-destructive font-semibold" : ""}>
                      {formatTimestamp(assignment.dueDate)}
                      {assignment.overdueDays && ` (${assignment.overdueDays} days overdue)`}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* --- POLICY/MEMO DETAILS CARD (RESTORED) --- */}
            <Card>
              <CardHeader>
                <CardTitle>{assignment.type} Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Type</span>
                  <Badge variant="outline">{assignment.type}</Badge>
                </div>

                {assignment.version && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Version</span>
                    <span>{assignment.version}</span>
                  </div>
                )}

                {assignment.number && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Number</span>
                    <span>{assignment.number}</span>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant="outline">{assignment.documentStatus}</Badge>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Signature Required</span>
                  <span>{assignment.requireSignature ? 'Yes' : 'No'}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Effective Date</span>
                  <span>{toSydneyDate(assignment.effectiveDate)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity Card */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(activityLogs || []).map(log => {
                    const user = getUserById(typeof log.user !== 'string' ? log.user.id : "");
                    return (
                      <div key={log.id} className="flex items-start gap-3">
                        <Avatar className="h-8 w-8 mt-1"><AvatarFallback>{user?.name.charAt(0) || '?'}</AvatarFallback></Avatar>
                        <div>
                          <p className="text-sm">{log.changeDescription}</p>
                          <p className="text-xs text-muted-foreground">{user?.name} • {formatTimestamp(log.timestamp)}</p>
                        </div>
                      </div>
                    );
                  })}
                  {activityLogs.length === 0 && <p className="text-sm text-center text-muted-foreground py-4">No recent activity.</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Add Users to Policy Modal */}
        {(policy || memo) && (
          <AddUsersToPolicyModal
            isOpen={showAddUsersModal}
            onClose={() => setShowAddUsersModal(false)}
            policy={policy}
            memo={memo}
            assignment={assignment}
            documentType={documentType}
            onSuccess={loadAssignmentDetails}
          />)}

        {(policy || memo) && (
          <AddUsersToPolicyModal
            isOpen={showRemoveUsersModal}
            onClose={() => setShowRemoveUsersModal(false)}
            policy={policy}
            memo={memo}
            assignment={assignment}
            isRemove={true}
            documentType={documentType}
            onSuccess={loadAssignmentDetails}
          />)}

        {/* Recall Confirmation Dialog */}
        <Dialog open={showRecallDialog} onOpenChange={setShowRecallDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Recall Policy
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to recall "{policy?.title}"?
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <h4 className="font-medium text-amber-800 mb-2">This action will:</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Change the policy status to "Draft"</li>
                  <li>• Delete all existing digital signatures</li>
                  <li>• Remove the policy from all users' "My Policies" list</li>
                  <li>• Allow you to edit and republish the policy</li>
                </ul>
              </div>

              <div className="flex justify-between gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowRecallDialog(false)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleRecallPolicy}
                  disabled={isProcessing}
                  className="flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                      Recalling...
                    </>
                  ) : (
                    <>
                      <Undo2 className="h-4 w-4" />
                      Recall Policy
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showDeleteAssignmentDialog} onOpenChange={setShowDeleteAssignmentDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Delete Policy Assignment
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete "{policy?.title}" assignment? This action cannot be undone.
              </p>

              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                <h4 className="font-medium text-destructive mb-2">Warning:</h4>
                <ul className="text-sm text-destructive/80 space-y-1">
                  <li>• This will permanently delete the policy assignment</li>
                  <li>• All associated data will be removed</li>
                  <li>• This action cannot be reversed</li>
                </ul>
              </div>

              <div className="flex justify-between gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteAssignmentDialog(false)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteAssignment(assignment.documentId)}
                  disabled={isProcessing}
                  className="flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete Assignment
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </Can>
  );
} 